import type {
  AuthTokenPayload,
  CookieConfig,
  ITokenService,
  PollingCallbacks,
  TokenStorageStrategy
} from './tokenContracts';

const COOKIE_CHECK_INTERVAL_MS = 60_000;
const EXPIRY_CHECK_INTERVAL_MS = 2_000;
const EXPIRY_BUFFER_MS = 30_000;
const DEFAULT_LIFETIME_MS = 30 * 60 * 1000;

export class TokenService implements ITokenService {
  private readonly config: CookieConfig;
  private strategy: TokenStorageStrategy;
  private expiresAt: Date | null = null;
  private tokenLifetimeMs: number | null = null;
  private cookieCheckInterval: ReturnType<typeof setInterval> | null = null;
  private expiryCheckInterval: ReturnType<typeof setInterval> | null = null;
  private onExpirySoon: (() => void) | null = null;
  private onTokenMissing: (() => void) | null = null;
  private onVisibilityChange: (() => void) | null = null;

  constructor(config: CookieConfig, strategy: TokenStorageStrategy) {
    this.config = config;
    this.strategy = strategy;
  }

  setTokens(payload: AuthTokenPayload): void {
    const expiresAt = new Date(payload.expiresIn * 1000);
    this.strategy.setAccessToken(payload.accessToken, expiresAt, this.config);
    this.strategy.setRefreshToken(payload.refreshToken, this.config);
    this.expiresAt = expiresAt;
    this.tokenLifetimeMs = expiresAt.getTime() - Date.now();
  }

  updateExpiry(expiresIn: number): void {
    const expiresAt = new Date(expiresIn * 1000);
    this.strategy.setAccessToken(this.getAccessToken(), expiresAt, this.config);
    this.expiresAt = expiresAt;
    this.tokenLifetimeMs = expiresAt.getTime() - Date.now();
  }

  extendTokenExpiry(): void {
    const accessToken = this.getAccessToken();
    if (!accessToken) {
      return;
    }
    const lifetimeMs = this.tokenLifetimeMs ?? DEFAULT_LIFETIME_MS;
    const newExpiresAt = new Date(Date.now() + lifetimeMs);
    this.strategy.setAccessToken(accessToken, newExpiresAt, this.config);
    this.expiresAt = newExpiresAt;
    if (!this.tokenLifetimeMs) {
      this.tokenLifetimeMs = lifetimeMs;
    }
  }

  getAccessToken(): string {
    return this.strategy.getAccessToken(this.config) ?? '';
  }

  getRefreshToken(): string {
    return this.strategy.getRefreshToken(this.config) ?? '';
  }

  getExpiresAt(): Date | null {
    return this.expiresAt;
  }

  isTokenValid(): boolean {
    return !!this.getAccessToken();
  }

  clearTokens(): void {
    this.strategy.clearAll(this.config);
    this.stopPolling();
    this.expiresAt = null;
  }

  setMobileTokens(accessToken: string, refreshToken: string): void {
    this.strategy.setAccessToken(accessToken, new Date(Date.now() + 86_400_000), this.config);
    this.strategy.setRefreshToken(refreshToken, this.config);
  }

  startPolling(callbacks: PollingCallbacks): void {
    this.stopPolling();
    this.onExpirySoon = callbacks.onExpirySoon;
    this.onTokenMissing = callbacks.onTokenMissing;

    this.onVisibilityChange = () => {
      if (!document.hidden && !this.getAccessToken()) {
        this.onTokenMissing?.();
      }
    };
    document.addEventListener('visibilitychange', this.onVisibilityChange);

    this.cookieCheckInterval = setInterval(() => {
      if (!this.getAccessToken()) {
        this.onTokenMissing?.();
      }
    }, COOKIE_CHECK_INTERVAL_MS);

    this.expiryCheckInterval = setInterval(() => {
      if (!this.expiresAt) {
        return;
      }
      if (this.expiresAt.getTime() - Date.now() < EXPIRY_BUFFER_MS) {
        this.onExpirySoon?.();
      }
    }, EXPIRY_CHECK_INTERVAL_MS);
  }

  stopPolling(): void {
    if (this.cookieCheckInterval) {
      clearInterval(this.cookieCheckInterval);
    }
    if (this.expiryCheckInterval) {
      clearInterval(this.expiryCheckInterval);
    }
    if (this.onVisibilityChange) {
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      this.onVisibilityChange = null;
    }
    this.cookieCheckInterval = null;
    this.expiryCheckInterval = null;
    this.onExpirySoon = null;
    this.onTokenMissing = null;
  }
}
